import React, { useState, useEffect, useRef } from 'react';
import axios from '../services/axiosInstance';
import { useAuth } from '../App';
import { Send, Paperclip, UserPlus, X, Search, Trash2, Edit2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const MessagesModal = ({ isOpen, onClose, mode = 'modal' }) => {
  const { user } = useAuth();
  const isPage = mode === 'page';
  
  const [conversations, setConversations] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      loadConversations();
      loadUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedConv || !isOpen) return;
    const interval = setInterval(() => loadMessages(selectedConv.user_id), 10000);
    return () => clearInterval(interval);
  }, [selectedConv, isOpen]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/messages/conversations');
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get('/users');
      // Backend returns {success, users:[...]} — kullanıcılar array'i.
      // Her item düz user objesi; role/position ayrı field. Eski `{user,role}`
      // sarmalayıcı formatına da geriye dönük destek verir.
      const raw = Array.isArray(data) ? data : (data?.users || []);
      const mapped = raw
        .map(item => {
          const u = item.user ? item.user : item;
          const roleName = item.role?.name || u.position || u.role_name || 'Belirtilmemiş';
          return { ...u, position: roleName };
        })
        .filter(u => u.id && u.id !== user.id && u.is_deleted !== true);
      setCompanyUsers(mapped);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadMessages = async (userId) => {
    try {
      const { data } = await axios.get(`/messages/conversation/${userId}`);
      setMessages(data);
      setTimeout(scrollToBottom, 100);
      await axios.put(`/messages/mark-conversation-read/${userId}`);
      await loadConversations();
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!messageInput.trim() || !selectedConv) return;
    
    try {
      setSending(true);
      if (editingMsgId) {
        await axios.put(`/messages/${editingMsgId}`, { content: messageInput });
        setEditingMsgId(null);
      } else {
        await axios.post('/messages', {
          receiver_id: selectedConv.user_id,
          content: messageInput
        });
      }
      setMessageInput('');
      await loadMessages(selectedConv.user_id);
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Mesaj gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Mesajı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`/messages/${messageId}`);
      await loadMessages(selectedConv.user_id);
      await loadConversations();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !selectedConv) return;
    if (file.size > 25 * 1024 * 1024) return toast.error('Dosya en fazla 25MB olmalı');
    // Artık tüm dosya tipleri kabul ediliyor — backend türü otomatik sınıflar
    // (image / pdf / file) ve UI'da uygun ikonla gösterir.

    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post('/messages/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const iconLabel =
        data.file_type === 'image' ? '📷 Resim' :
        data.file_type === 'pdf' ? '📄 PDF' :
        `📎 ${data.file_name || 'Dosya'}`;

      await axios.post('/messages', {
        receiver_id: selectedConv.user_id,
        content: iconLabel,
        file_url: data.file_url,
        file_name: data.file_name,
        file_type: data.file_type,
        file_size: data.file_size
      });

      await loadMessages(selectedConv.user_id);
      await loadConversations();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Dosya yüklenemedi.');
    }
  };

  const startConversation = (u) => {
    setSelectedConv({
      user_id: u.id,
      user_name: u.full_name,
      user_email: u.email,
      user_position: u.position
    });
    setShowNewChat(false);
    setUserSearchQuery('');
    const existing = conversations.find(c => c.user_id === u.id);
    existing ? loadMessages(u.id) : setMessages([]);
  };

  const formatTime = (ts) => {
    const diffMins = Math.floor((new Date() - new Date(ts)) / 60000);
    if (diffMins < 1) return 'Şimdi';
    if (diffMins < 60) return `${diffMins}dk`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}s`;
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}g`;
    return new Date(ts).toLocaleDateString('tr-TR');
  };

  const filteredConvs = conversations.filter(c =>
    c.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = companyUsers.filter(u =>
    u.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  // Sidebar'da mevcut konuşmalar + henüz konuşulmamış diğer şirket kullanıcıları
  // birleştirilerek gösterilir. Kullanıcı "Yeni Sohbet" modalına girmeden
  // istediği kişiyi doğrudan seçip mesaj gönderebilir.
  const conversationUserIds = new Set(conversations.map(c => c.user_id));
  const usersWithoutConvo = companyUsers
    .filter(u => !conversationUserIds.has(u.id))
    .filter(u => (u.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .map(u => ({
      user_id: u.id,
      user_name: u.full_name,
      user_position: u.position,
      last_message: null,
      last_message_time: null,
      unread_count: 0,
      _no_convo: true,
    }));
  const sidebarItems = [...filteredConvs, ...usersWithoutConvo];

  if (!isOpen && !isPage) return null;

  // Container classes differ between modal (fixed overlay) and page (in-flow)
  const outerCls = isPage
    ? 'w-full'
    : 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4';
  const innerCls = isPage
    ? 'bg-white rounded-2xl border border-gray-200 shadow-sm w-full h-[calc(100vh-220px)] min-h-[600px] flex overflow-hidden'
    : 'bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[85vh] flex overflow-hidden';

  return (
    <div 
      className={outerCls}
      data-testid={isPage ? 'messages-page' : 'messages-modal'}
      onClick={(e) => {
        if (!isPage && modalRef.current && !modalRef.current.contains(e.target)) onClose();
      }}
    >
      <div 
        ref={modalRef}
        className={innerCls}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sidebar */}
        <div className="w-80 bg-gray-50 border-r flex flex-col">
          <div className="p-4 bg-white border-b">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg font-bold">Mesajlar</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowNewChat(true)}
                  className="p-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  <UserPlus className="w-4 h-4" />
                </button>
                {!isPage && (
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : sidebarItems.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                {companyUsers.length === 0 ? 'Şirketinizde başka kullanıcı yok' : 'Sonuç bulunamadı'}
              </div>
            ) : (
              sidebarItems.map((c) => (
                <div
                  key={c.user_id}
                  onClick={() => {
                    setSelectedConv(c);
                    loadMessages(c.user_id);
                    setShowNewChat(false);
                  }}
                  className={`p-3 border-b cursor-pointer hover:bg-white ${
                    selectedConv?.user_id === c.user_id ? 'bg-white border-l-4 border-l-teal-500' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {c.user_name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate">{c.user_name}</h3>
                          {c.user_position && (
                            <p className="text-xs text-gray-500 truncate">{c.user_position}</p>
                          )}
                        </div>
                        {c.last_message_time && (
                          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{formatTime(c.last_message_time)}</span>
                        )}
                      </div>
                      {c._no_convo ? (
                        <p className="text-xs text-gray-400 italic truncate">Mesajlaşmaya başla</p>
                      ) : (
                        <p className="text-xs text-gray-600 truncate">
                          {c.last_sender_id === user.id && 'Sen: '}
                          {c.last_message}
                        </p>
                      )}
                      {c.unread_count > 0 && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs text-white bg-teal-500 rounded-full">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Area */}
        <div className="flex-1 flex flex-col">
          {showNewChat ? (
            <div className="flex flex-col h-full">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold mb-3">Yeni Sohbet</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Kullanıcı ara..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {filteredUsers.length === 0 ? (
                  <div className="text-center text-gray-500">Kullanıcı bulunamadı</div>
                ) : (
                  <div className="space-y-2">
                    {filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => startConversation(u)}
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 hover:border-teal-300"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-bold">
                            {u.full_name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold truncate">{u.full_name}</h4>
                            {u.position ? (
                              <p className="text-sm text-teal-600 font-medium truncate">{u.position}</p>
                            ) : (
                              <p className="text-sm text-gray-500 truncate">{u.email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : selectedConv ? (
            <div className="flex flex-col h-full">
              <div className="p-4 border-b flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                  {selectedConv.user_name?.charAt(0) || '?'}
                </div>
                <div>
                  <h3 className="font-semibold">{selectedConv.user_name}</h3>
                  {selectedConv.user_position && <p className="text-sm text-gray-500">{selectedConv.user_position}</p>}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Henüz mesaj yok</p>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_id === user.id;
                    return (
                      <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                          <div className={`rounded-2xl px-4 py-2 ${isOwn ? 'bg-teal-500 text-white' : 'bg-white border'}`}>
                            {msg.file_url && (
                              <div className="mb-2">
                                {msg.file_type === 'image' ? (
                                  <img
                                    src={msg.file_url}
                                    alt={msg.file_name}
                                    className="rounded-lg max-w-full h-auto max-h-48 cursor-pointer"
                                    onClick={() => window.open(msg.file_url, '_blank')}
                                  />
                                ) : (
                                  <a
                                    href={msg.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 text-sm ${isOwn ? 'text-white' : 'text-teal-600'}`}
                                  >
                                    <Paperclip className="w-4 h-4" />
                                    {msg.file_name}
                                  </a>
                                )}
                              </div>
                            )}
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span>{formatTime(msg.created_at)}</span>
                            {isOwn && (
                              <>
                                {!msg.file_url && (
                                  <button onClick={() => { setEditingMsgId(msg.id); setMessageInput(msg.content); }}>
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                )}
                                <button onClick={() => handleDeleteMessage(msg.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white border-t">
                {editingMsgId && (
                  <div className="mb-2 flex items-center justify-between bg-teal-50 px-3 py-2 rounded-lg">
                    <span className="text-sm text-teal-700">Düzenleniyor...</span>
                    <button onClick={() => { setEditingMsgId(null); setMessageInput(''); }}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-3 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Mesajınızı yazın..."
                    rows={1}
                    className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                    style={{ minHeight: '48px', maxHeight: '120px' }}
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim() || sending}
                    className="p-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
                  >
                    {sending ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center">
                <MessageSquare className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">Mesajlaşmaya başlayın</h3>
                <button
                  onClick={() => setShowNewChat(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  <UserPlus className="w-4 h-4" />
                  Yeni Sohbet
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesModal;
